import type { Value, TElement } from 'platejs';

export type SectionInfo = {
  sectionTitle: string;
  sectionIndex: number;
  startBlockIndex: number;
  endBlockIndex: number;
  blocks: Value;
};

/**
 * Given a PlateJS Value and a block index, find the H2 section that contains it.
 * Returns null if the value is empty.
 */
export function findSectionForBlock(
  plateValue: Value,
  blockIndex: number,
): SectionInfo | null {
  if (!plateValue || plateValue.length === 0) return null;

  const sections: SectionInfo[] = [];
  let currentStart = 0;
  let currentTitle = 'Preamble';
  let sectionIdx = 0;

  for (let i = 0; i < plateValue.length; i++) {
    const node = plateValue[i] as TElement;
    if (node.type === 'h2' && i > 0) {
      sections.push({
        sectionTitle: currentTitle,
        sectionIndex: sectionIdx,
        startBlockIndex: currentStart,
        endBlockIndex: i - 1,
        blocks: plateValue.slice(currentStart, i),
      });
      sectionIdx++;
      currentStart = i;
      currentTitle = extractNodeText(node);
    } else if (node.type === 'h2' && i === 0) {
      currentTitle = extractNodeText(node);
    }
  }

  sections.push({
    sectionTitle: currentTitle,
    sectionIndex: sectionIdx,
    startBlockIndex: currentStart,
    endBlockIndex: plateValue.length - 1,
    blocks: plateValue.slice(currentStart),
  });

  for (const section of sections) {
    if (blockIndex >= section.startBlockIndex && blockIndex <= section.endBlockIndex) {
      return section;
    }
  }

  return sections[sections.length - 1] ?? null;
}

/**
 * Convert a PlateJS Value (array of nodes) to markdown text.
 */
export function plateValueToMarkdown(value: Value): string {
  const lines: string[] = [];

  for (const node of value) {
    const el = node as TElement;
    lines.push(nodeToMarkdown(el));
  }

  return lines.join('\n\n');
}

function extractNodeText(node: unknown): string {
  if (!node || typeof node !== 'object') return '';
  const n = node as { text?: string; children?: unknown[] };
  if (typeof n.text === 'string') return n.text;
  if (Array.isArray(n.children)) {
    return n.children.map(extractNodeText).join('');
  }
  return '';
}

function leafToMarkdown(leaf: unknown): string {
  if (!leaf || typeof leaf !== 'object') return '';
  const l = leaf as Record<string, unknown>;
  let text = typeof l.text === 'string' ? l.text : '';
  if (!text) {
    if (Array.isArray(l.children)) {
      text = (l.children as unknown[]).map(leafToMarkdown).join('');
    }
  }
  if (l.bold) text = `**${text}**`;
  if (l.italic) text = `*${text}*`;
  if (l.code) text = `\`${text}\``;
  if (l.strikethrough) text = `~~${text}~~`;
  return text;
}

function childrenToMarkdown(children: unknown[]): string {
  return children.map(leafToMarkdown).join('');
}

function nodeToMarkdown(node: TElement): string {
  const children = Array.isArray(node.children)
    ? childrenToMarkdown(node.children as unknown[])
    : '';

  switch (node.type) {
    case 'h1': return `# ${children}`;
    case 'h2': return `## ${children}`;
    case 'h3': return `### ${children}`;
    case 'blockquote': return `> ${children}`;
    case 'hr': return '---';
    case 'code_block': return `\`\`\`\n${children}\n\`\`\``;
    case 'ul':
      return listToMarkdown(node, '- ');
    case 'ol':
      return listToMarkdown(node, null);
    case 'li':
    case 'lic':
      return children;
    case 'table':
      return tableToMarkdown(node);
    case 'img':
      return `![](${(node as { url?: string }).url ?? ''})`;
    case 'p':
    default:
      return children;
  }
}

function listToMarkdown(node: TElement, bullet: string | null): string {
  const items = Array.isArray(node.children) ? (node.children as TElement[]) : [];
  return items
    .map((item, i) => {
      const text = extractNodeText(item);
      const prefix = bullet ?? `${i + 1}. `;
      return `${prefix}${text}`;
    })
    .join('\n');
}

function tableToMarkdown(node: TElement): string {
  const rows = Array.isArray(node.children) ? (node.children as TElement[]) : [];
  if (rows.length === 0) return '';

  const lines: string[] = [];
  rows.forEach((row, rowIdx) => {
    const cells = Array.isArray(row.children) ? (row.children as TElement[]) : [];
    const cellTexts = cells.map((cell) => extractNodeText(cell));
    lines.push(`| ${cellTexts.join(' | ')} |`);
    if (rowIdx === 0) {
      lines.push(`| ${cellTexts.map(() => '---').join(' | ')} |`);
    }
  });

  return lines.join('\n');
}
